import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import { UserSquare2, Plus, Pencil, Trash2 } from 'lucide-react'

import { Main } from '@/components/layout/main'
import { PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/table'
import DataTableSkeleton from '@/components/ui/data-table-skeleton'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { useToast } from '@/hooks/use-toast'
import { externalLawyersService, ExternalLawyer } from '@/api/external-lawyers'
import { ExternalLawyerDialog } from './components/external-lawyer-dialog'

export default function ExternalLawyersPage() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [dialog, setDialog] = useState<{ open: boolean; lawyer?: ExternalLawyer }>({ open: false })
  const [deleting, setDeleting] = useState<ExternalLawyer | null>(null)

  const queryKey = ['external-lawyers']

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => externalLawyersService.list({ limit: 200 }),
  })

  const lawyers = data?.results ?? []

  const handleDelete = async () => {
    if (!deleting) return
    try {
      await externalLawyersService.delete(deleting.id)
      toast({ title: t('ext_deleted') })
      queryClient.invalidateQueries({ queryKey })
    } finally {
      setDeleting(null)
    }
  }

  const columns = useMemo<ColumnDef<ExternalLawyer>[]>(() => [
    {
      id: 'name',
      header: t('ext_col_name'),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{row.original.name}</span>
          <Badge variant="secondary" className="text-xs font-normal">
            {t('ext_badge')}
          </Badge>
        </div>
      ),
    },
    {
      id: 'email',
      header: t('ext_col_email'),
      cell: ({ row }) =>
        row.original.email ? (
          <span className="text-sm text-muted-foreground">{row.original.email}</span>
        ) : (
          <span className="text-xs text-muted-foreground/50">—</span>
        ),
    },
    {
      id: 'created_at',
      header: t('ext_col_created'),
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {new Date(row.original.created_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex items-center gap-1 justify-end">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => {
              e.stopPropagation()
              setDialog({ open: true, lawyer: row.original })
            }}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation()
              setDeleting(row.original)
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ], [t])

  return (
    <Main>
      <PageHeader
        leading={
          <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <UserSquare2 className="h-5 w-5" />
          </span>
        }
        title={t('external_lawyers')}
        description={t('external_lawyers_desc')}
        actions={
          <Button onClick={() => setDialog({ open: true })}>
            <Plus className="me-2 h-4 w-4" />
            {t('ext_add')}
          </Button>
        }
      />

      <div className="-mx-4 flex-1 overflow-auto px-4 py-1">
        {isLoading ? (
          <DataTableSkeleton columns={4} rows={8} />
        ) : lawyers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-dashed border-slate-300 dark:border-slate-700">
            <UserSquare2 className="h-10 w-10 mb-2 opacity-20" />
            <p className="text-sm">{t('ext_no_data')}</p>
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={lawyers}
            pages={1}
            params={{ page: 0, page_size: lawyers.length }}
            setParams={() => {}}
            showPagination={false}
          />
        )}
      </div>

      <ExternalLawyerDialog
        open={dialog.open}
        lawyer={dialog.lawyer}
        onClose={() => setDialog({ open: false })}
        onSaved={() => queryClient.invalidateQueries({ queryKey })}
      />

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        title={t('ext_delete')}
        desc={t('ext_delete_desc')}
        destructive
        handleConfirm={handleDelete}
      />
    </Main>
  )
}
